// One recovery scheduler for the operational LiveView and optional Kiosk channel.
// No offline storage, operation queue or hard navigation is used here.
export class DeviceConnection {
  constructor(liveSocket, { document: doc = document, window: win = window } = {}) {
    this.liveSocket = liveSocket;
    this.doc = doc;
    this.win = win;
    this.state = 'syncing';
    this.generation = 0;
    this.attempts = 0;
    this.lastAttemptAt = Date.now();
    this.recovering = true;
    this.network = null;
    this.hook = null;
    this.timer = null;
    this.probeTimer = null;
    this.capability = null;
    this.listeners = [];
    this.socket = liveSocket.getSocket();
    this.refs = [
      this.socket.onClose(() => this.lost({ live: true })),
      this.socket.onError(() => this.lost({ live: true }))
    ];
    this.listen(win, 'offline', () => this.lost({ live: true, channel: true }));
    this.listen(win, 'online', () => {
      if (this.state === 'reconnecting' && !this.probing) this.schedule(0);
    });
    this.listen(win, 'phx:page-loading-start', (event) => {
      if (event.detail?.kind === 'error') this.lost();
    });
    this.listen(win, 'device:connection-lost', () => this.lost());
    this.listen(win, 'device:connection-joined', () => this.probe());
    this.listen(doc, 'visibilitychange', () => {
      if (!doc.hidden && this.state === 'reconnecting' && !this.probing) this.schedule(0);
    });
    for (const name of ['click', 'pointerdown', 'touchstart', 'keydown', 'keyup', 'submit', 'input', 'change']) {
      this.listen(win, name, (event) => {
        if (!this.blocked()) return;
        if (event.cancelable) event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, passive: false });
    }
    this.render();
    this.schedule(1000);
  }

  listen(target, event, callback, options) {
    target.addEventListener(event, callback, options);
    this.listeners.push(() => target.removeEventListener(event, callback, options));
  }

  blocked() { return this.state !== 'ready'; }

  capabilityStorageKey() {
    const location = this.win.location || {};
    return `mealflow:device-capability:${location.pathname || '/'}${location.search || ''}`;
  }

  readCapability() {
    const fragment = new URLSearchParams((this.win.location?.hash || '').replace(/^#/, ''));
    const fromUrl = fragment.get('capability');
    if (fromUrl) return fromUrl;
    if (this.capability) return this.capability;

    try {
      return this.win.sessionStorage?.getItem(this.capabilityStorageKey()) || null;
    } catch (_) {
      return null;
    }
  }

  clearCapabilityFromUrl() {
    const location = this.win.location || {};
    const fragment = new URLSearchParams((location.hash || '').replace(/^#/, ''));
    if (!fragment.has('capability') || !this.win.history?.replaceState || !location.pathname) return;

    fragment.delete('capability');
    const rest = fragment.toString();
    const path = `${location.pathname}${location.search || ''}${rest ? `#${rest}` : ''}`;
    this.win.history.replaceState(this.win.history.state, '', path);
  }

  rememberCapability(capability) {
    if (!capability) return;
    this.capability = capability;
    try {
      this.win.sessionStorage?.setItem(this.capabilityStorageKey(), capability);
    } catch (_) { /* In-memory access still works until refresh. */ }
    this.clearCapabilityFromUrl();
  }

  forgetCapability() {
    this.capability = null;
    try {
      this.win.sessionStorage?.removeItem(this.capabilityStorageKey());
    } catch (_) { /* Revocation is enforced by the server. */ }
    this.clearCapabilityFromUrl();
  }

  canConnectNetwork(network) {
    return this.state !== 'denied' && this.network === network && Date.now() >= (this.notBefore || 0);
  }

  attach(hook) {
    clearTimeout(this.probeTimer);
    this.probeTimer = null;
    this.probing = false;
    this.hook = hook;
    this.hookConnected = true;
    this.kind = hook.el.dataset.deviceKind;
    this.channelRequired = hook.el.dataset.channelRequired === 'true';
    this.generation++;
    this.recovering = true;
    this.setState('syncing');
    this.probe();
  }

  detach(hook) {
    if (this.hook !== hook) return;
    this.hook = null;
    this.lost();
  }

  registerNetwork(network) {
    if (this.network && this.network !== network) {
      this.network.stopTransport();
      this.generation++;
      clearTimeout(this.probeTimer);
      this.probeTimer = null;
      this.probing = false;
      this.recovering = true;
      this.setState('syncing');
      this.schedule(1000);
    }
    this.network = network;
    this.probe();
  }

  lost({ live = false, channel = false, retryAfterMs = 0 } = { live: true }) {
    if (this.stopping || this.state === 'denied') return;
    this.interrupted = this.interrupted || !!this.doc.querySelector('[data-phx-ref-loading]');
    this.generation++;
    if (live) { this.hookConnected = false; this.liveNeedsReconnect = true; }
    if (channel) this.channelNeedsReconnect = true;
    this.notBefore = Math.max(this.notBefore || 0, Date.now() + retryAfterMs);
    this.recovering = true;
    clearTimeout(this.probeTimer);
    this.probeTimer = null;
    this.probing = false;
    this.setState('reconnecting');
    // The next task runs after Phoenix has scheduled its own fallback. Public
    // disconnect cancels that fallback and its transport retry timer.
    const generation = this.generation;
    queueMicrotask(() => {
      if (generation !== this.generation) return;
      if (this.stopping || this.state === 'ready' || this.state === 'denied') return;
      this.stopping = true;
      if (this.liveNeedsReconnect) this.liveSocket.disconnect();
      if (this.network && this.channelNeedsReconnect) this.network.stopTransport();
      this.stopping = false;
    });
    if (retryAfterMs > 0 || !this.timer) this.schedule(this.attempts === 0 ? 0 : this.retryDelay());
  }

  retryDelay() {
    const base = Math.min(1000 * 2 ** Math.min(Math.max(this.attempts - 1, 0), 5), 30000);
    return Math.min(30000, Math.round(base * (0.9 + Math.random() * 0.2)));
  }

  schedule(delay) {
    if (this.stopping || this.state === 'denied') return;
    clearTimeout(this.timer);
    const wait = Math.max(delay, (this.notBefore || 0) - Date.now());
    this.timer = setTimeout(() => { this.timer = null; this.attempt(); }, wait);
  }

  attempt() {
    if (this.stopping || this.state === 'denied' || this.probing) return;
    if (Date.now() < (this.notBefore || 0)) { this.schedule(0); return; }
    // Give a connection/join already in progress its deadline. A one-second
    // retry must not continually abort a slower server mount or channel join.
    const elapsed = Date.now() - (this.lastAttemptAt || 0);
    const joining = this.socket.connectionState?.() === 'connecting' ||
      (this.liveSocket.isConnected() && !this.hookConnected) || this.network?._connecting;
    if (joining && elapsed < 8000) { this.schedule(8000 - elapsed); return; }
    this.lastAttemptAt = Date.now();
    this.generation++;
    this.recovering = true;
    this.setState('reconnecting');
    this.attempts++;
    this.stopping = true;
    const restartLive = this.liveNeedsReconnect || !this.liveSocket.isConnected();
    if (restartLive) { this.hookConnected = false; this.liveSocket.disconnect(); }
    if (this.network && this.channelNeedsReconnect) this.network.stopTransport();
    this.stopping = false;
    this.liveNeedsReconnect = false;
    this.channelNeedsReconnect = false;
    if (restartLive) this.liveSocket.connect();
    if (this.network && !this.network.isConnected) this.network.connect();
    this.render();
    this.probe();
    // Retries never end for transient failure. One timer owns both transports.
    if (this.state !== 'ready' && !this.probing) this.schedule(this.retryDelay());
  }

  probe() {
    if (this.stopping || this.state === 'denied' || this.probing || !this.hook || !this.hookConnected ||
        !this.liveSocket.isConnected() || (this.channelRequired && !this.network?.isConnected)) return;
    const generation = this.generation;
    const hook = this.hook;
    this.probing = true;
    clearTimeout(this.timer);
    this.timer = null;
    if (this.recovering) this.setState('syncing');
    clearTimeout(this.probeTimer);
    this.probeTimer = setTimeout(() => this.lost({}), 8000);
    const capability = this.readCapability();
    const reply = (response) => {
      if (generation !== this.generation || hook !== this.hook || !this.probing) return;
      this.probing = false;
      this.lastReply = response;
      clearTimeout(this.probeTimer);
      this.probeTimer = null;
      if (response?.status === 'denied') {
        this.deny();
      } else if (response?.status === 'ready') {
        this.rememberCapability(capability);
        clearTimeout(this.timer);
        this.timer = null;
        this.attempts = 0;
        this.notBefore = 0;
        this.liveNeedsReconnect = false;
        this.channelNeedsReconnect = false;
        this.recovering = false;
        this.interrupted = false;
        this.setState('ready');
        this.probeTimer = setTimeout(() => { this.probeTimer = null; this.probe(); }, 15000);
      } else {
        this.lost({});
      }
    };
    try {
      const result = hook.pushEvent('device_connection_ready', {
        recovering: this.recovering,
        interrupted: !!this.interrupted,
        device_secret: this.network?.readDeviceAccessSecret(),
        capability
      }, reply);
      result?.catch?.(() => {
        if (generation === this.generation && hook === this.hook && this.probing) this.lost({});
      });
    } catch (_) { this.lost({}); }
  }

  deny() {
    this.forgetCapability();
    this.generation++;
    clearTimeout(this.timer);
    clearTimeout(this.probeTimer);
    this.timer = null;
    this.probeTimer = null;
    this.probing = false;
    this.setState('denied');
    // Keep the LiveView transport alive for the server's revocation diff.
    // The denied overlay blocks browser input; AccessGuard rejects events.
    this.network?.stopTransport();
  }

  setState(state) {
    const changed = this.state !== state;
    this.state = state;
    this.render();
    if (changed) this.win.dispatchEvent(new CustomEvent('device:connection-state', { detail: { state } }));
  }

  render() {
    this.doc.documentElement.dataset.deviceConnection = this.state;
    let dialog = this.doc.getElementById('device-connection-modal');
    if (!dialog) {
      dialog = this.doc.createElement('dialog');
      dialog.id = 'device-connection-modal';
      dialog.className = 'device-connection-modal';
      dialog.setAttribute('aria-labelledby', 'device-connection-title');
      dialog.setAttribute('aria-describedby', 'device-connection-description');
      dialog.innerHTML = `<div class="device-connection-card"><div class="device-connection-orbit" aria-hidden="true"><span class="device-connection-emoji">📡</span><span class="device-connection-spark">✨</span></div><p class="device-connection-label">MEALFLOW · LIGAÇÃO</p><h2 id="device-connection-title"></h2><p id="device-connection-description"></p><div class="device-connection-progress" aria-hidden="true"><span></span></div><p class="device-connection-detail" role="status" aria-live="polite"></p><p class="device-connection-footnote">As operações estão temporariamente bloqueadas.</p></div>`;
      dialog.addEventListener('cancel', (event) => event.preventDefault());
      this.doc.body.appendChild(dialog);
    }
    const denied = this.state === 'denied';
    const syncing = this.state === 'syncing';
    const coolingDown = !denied && Date.now() < (this.notBefore || 0);
    dialog.dataset.state = this.state;
    dialog.querySelector('#device-connection-title').textContent = denied ? 'Acesso ao dispositivo bloqueado' : coolingDown ? 'A aguardar nova tentativa' : syncing ? 'A sincronizar com o MealFlow' : 'A restabelecer ligação';
    dialog.querySelector('#device-connection-description').textContent = denied ? 'A ligação segura deixou de ser válida ou o dispositivo foi desativado. Contacte o administrador.' : coolingDown ? 'O serviço pediu uma pausa nas tentativas. A recuperação continuará automaticamente após esse prazo.' : syncing ? 'Estamos a confirmar o acesso e a atualizar os dados antes de continuar.' : 'A ligação ao MealFlow foi interrompida. Estamos a tentar recuperar automaticamente.';
    dialog.querySelector('.device-connection-detail').textContent = denied ? '🔒 É necessária intervenção do administrador' : syncing ? '🔄 A verificar o serviço' : `🔄 Tentativa ${Math.max(this.attempts, 1)} · recuperação automática`;
    if (this.blocked() && !dialog.open) dialog.showModal();
    if (!this.blocked() && dialog.open) dialog.close();
  }

  destroy() {
    this.stopping = true;
    clearTimeout(this.timer);
    clearTimeout(this.probeTimer);
    this.listeners.forEach((remove) => remove());
    this.socket.off(this.refs);
    this.doc.getElementById('device-connection-modal')?.remove();
    delete this.doc.documentElement.dataset.deviceConnection;
  }
}

export const DeviceConnectionHook = {
  mounted() { window.deviceConnection?.attach(this); },
  disconnected() { window.deviceConnection?.lost(); },
  reconnected() { window.deviceConnection?.attach(this); },
  destroyed() { window.deviceConnection?.detach(this); }
};

export const isDeviceCommandBlocked = () => !!window.deviceConnection?.blocked();

export const deviceLiveSocketOptions = {
  // Allow the supervisor to cancel Phoenix's hard-reload fallback through the
  // public disconnect API, including a push timeout with no error DOM event.
  reloadJitterMin: 30000,
  reloadJitterMax: 40000,
  failsafeJitter: 40000,
  disconnectedTimeout: 0
};
